/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AccountDeletion() {
  const { user, token, logout } = useAuth();
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onDelete = async () => {
    if (confirm !== "DELETE MY ACCOUNT") {
      toast.error("Type DELETE MY ACCOUNT exactly to confirm.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.delete(`${API}/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Your account has been scheduled for deletion. You'll be signed out now.");
      setTimeout(() => {
        logout();
        window.location.href = "/";
      }, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete account. Email privacy@thecauldron.app to delete manually.");
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-8" data-testid="account-deletion-page">
        <header>
          <div className="label-mystic text-secondary">Privacy · Account Deletion</div>
          <h1 className="font-serif text-5xl mt-3">Delete Your Cauldron Account</h1>
          <p className="text-foreground/65 mt-4 leading-relaxed">
            We respect your right to be forgotten. You can delete your Cauldron account and request erasure of your personal data at any time from this page — no email, no support ticket, no waiting on a human.
          </p>
        </header>

        <section className="parchment p-6">
          <h2 className="font-serif text-2xl mb-3 text-secondary">What Gets Deleted (Within 30 Days)</h2>
          <ul className="list-disc pl-5 space-y-1.5 text-sm leading-relaxed text-foreground/80">
            <li>Your profile, avatar, banner, bio, business details, lineage info</li>
            <li>Your posts, clips, comments, oracle readings</li>
            <li>Your direct messages (your side of the conversation is anonymized in the recipient's inbox)</li>
            <li>Your saved items, follows, favorites, notifications</li>
            <li>Your wallet balance and CAUL ledger (any pending payouts must be claimed first)</li>
            <li>Your KYC documents (Persona purges within 30 days; we keep only the approval status code)</li>
            <li>Your push notification tokens and device records</li>
            <li>Your email subscription preferences and ambassador referral records</li>
          </ul>
        </section>

        <section className="parchment p-6">
          <h2 className="font-serif text-2xl mb-3 text-secondary">What We Retain (and Why)</h2>
          <ul className="list-disc pl-5 space-y-1.5 text-sm leading-relaxed text-foreground/80">
            <li><strong>Payment transactions</strong> — up to 7 years to comply with IRS, AML, and Stripe record-keeping requirements. Stored encrypted, access-restricted.</li>
            <li><strong>Tax forms (1099-K, 1099-MISC)</strong> — up to 7 years (US tax law)</li>
            <li><strong>Trust &amp; Safety records</strong> — for users banned for fraud, harassment, or CSAM, we retain a minimal identifier (email hash, IP hash, ban reason) indefinitely to prevent re-registration</li>
            <li><strong>Audit logs</strong> — admin actions are retained for 2 years</li>
            <li><strong>Aggregated analytics</strong> — fully anonymized usage statistics (cannot be tied back to you)</li>
          </ul>
        </section>

        <section className="parchment p-6">
          <h2 className="font-serif text-2xl mb-3 text-secondary">Before You Delete</h2>
          <ul className="list-disc pl-5 space-y-1.5 text-sm leading-relaxed text-foreground/80">
            <li>Cancel any active subscription (Profile → Subscription)</li>
            <li>Claim any pending payout (Wallet → Cashout) — once deleted, payouts cannot be re-issued</li>
            <li>Download a copy of your content if you want to keep it (Profile → Data Export — request via privacy@thecauldron.app)</li>
            <li>Notify any clients with upcoming bookings — they will receive a system notification but you may want to message them personally</li>
          </ul>
        </section>

        {user ? (
          <section className="parchment p-6 border-2 border-destructive/40">
            <h2 className="font-serif text-2xl mb-3 text-destructive">Permanently Delete My Account</h2>
            <p className="text-sm text-foreground/80 mb-4">
              You are signed in as <strong>{user.email}</strong>. This action cannot be undone. Type{" "}
              <code className="bg-foreground/10 px-1.5 py-0.5 rounded text-secondary">DELETE MY ACCOUNT</code> below to confirm.
            </p>
            <input
              type="text"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="DELETE MY ACCOUNT"
              data-testid="account-delete-confirm-input"
              className="w-full bg-background/40 border border-foreground/20 rounded px-3 py-2 text-sm mb-3"
            />
            <Button
              variant="destructive"
              disabled={confirm !== "DELETE MY ACCOUNT" || submitting}
              onClick={onDelete}
              data-testid="account-delete-submit-btn"
            >
              {submitting ? "Deleting…" : "Delete My Account Permanently"}
            </Button>
          </section>
        ) : (
          <section className="parchment p-6">
            <h2 className="font-serif text-2xl mb-3 text-secondary">Sign In to Delete</h2>
            <p className="text-sm text-foreground/80 mb-4">
              You must be signed in to delete your account through the app. Alternatively, you can email{" "}
              <a href="mailto:privacy@thecauldron.app" className="text-secondary underline">privacy@thecauldron.app</a>{" "}
              from your registered email address and we will process the deletion within 7 business days.
            </p>
            <a href="/auth?mode=login">
              <Button data-testid="account-deletion-signin-btn">Sign In</Button>
            </a>
          </section>
        )}

        <section className="parchment p-6">
          <h2 className="font-serif text-2xl mb-3 text-secondary">Alternative: Email Request</h2>
          <p className="text-sm text-foreground/80">
            If you cannot access your account (lost password, locked out, deactivated by us), email{" "}
            <a href="mailto:privacy@thecauldron.app" className="text-secondary underline">privacy@thecauldron.app</a>{" "}
            from your registered email address with subject line "Delete My Account." We will verify your identity and process the deletion within 7 business days.
          </p>
        </section>
      </div>
    </Layout>
  );
}
